import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Placements, Popper } from '@videoblocks/storywind';

import { selectShowAnimatedAiThumbnailButton } from '../../../../selectors/searchSelectors';
import { useStockItemCompact } from '../hooks/useStockItemCompact';

export const AIEditButton = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { isCompact, containerRef } = useStockItemCompact();
  const showAnimatedAiThumbnailButton = useSelector(
    selectShowAnimatedAiThumbnailButton
  );
  const defaultButtonClassName = twMerge(
    'bg-gray-900 hover:bg-opacity-90 text-white rounded-full flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white',
    !showAnimatedAiThumbnailButton && 'bg-opacity-80'
  );

  const disabledButtonClassName =
    'bg-gray-200 text-gray-500 rounded-full flex items-center justify-center cursor-not-allowed';

  if (isCompact && !showAnimatedAiThumbnailButton) {
    // Compact version with icon only and tooltip
    return (
      <div ref={containerRef}>
        <Popper
          placement={Placements.Top}
          popperClasses="px-2 py-1 text-white bg-black rounded opacity-90 text-sm"
          showContent={showTooltip}
          triggerElement={(referenceElement) => (
            <div
              className="relative p-0.5 rounded-full"
              style={{
                display: 'inline-block',
                background: `${
                  disabled
                    ? ''
                    : 'linear-gradient(to right, #9952E0, #FAE3D7, #F66E6E)'
                }`,
              }}
              ref={referenceElement}
            >
              <button
                disabled={disabled}
                className={`${
                  disabled ? disabledButtonClassName : defaultButtonClassName
                } w-10 h-10 relative`}
                data-cy="ai-edit-button"
                onClick={onClick}
                onMouseEnter={() => setShowTooltip(true)}
                onFocus={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onBlur={() => setShowTooltip(false)}
                aria-label="Edit with AI"
              >
                <img
                  src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/${
                    disabled ? 'ai-wand-disabled.svg' : 'ai-wand.svg'
                  }`}
                  alt="Edit with AI"
                  className="w-4 h-4"
                />
              </button>
            </div>
          )}
        >
          <div id="tooltip-ai-edit">Edit with AI</div>
        </Popper>
      </div>
    );
  }

  // Full version with text
  return (
    <div ref={containerRef}>
      {showAnimatedAiThumbnailButton && (
        <style>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
      )}
      <div
        className={twMerge(
          'relative group',
          showAnimatedAiThumbnailButton ? '' : 'p-0.5 rounded-full'
        )}
        style={{
          background: `${
            disabled || showAnimatedAiThumbnailButton
              ? ''
              : 'linear-gradient(to right, #9952E0, #FAE3D7, #F66E6E)'
          }`,
        }}
      >
        {showAnimatedAiThumbnailButton && !disabled && (
          <>
            <div
              className="absolute rounded-full overflow-hidden opacity-100 group-hover:opacity-0 transition-opacity duration-300"
              style={{
                top: '-2px',
                right: '-2px',
                bottom: '-2px',
                left: '-2px',
              }}
            >
              <div
                className="absolute"
                style={{
                  width: '200%',
                  height: '200%',
                  left: '-50%',
                  top: '-50%',
                  background:
                    'conic-gradient(transparent 0deg, transparent 270deg, rgb(153, 82, 224) 300deg, rgb(250, 227, 215) 330deg, rgb(255, 102, 230) 345deg, transparent 360deg)',
                  animation: 'spin-slow 3s linear infinite',
                }}
              />
            </div>
            <div
              className="absolute rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                top: '-2px',
                right: '-2px',
                bottom: '-2px',
                left: '-2px',
                background:
                  'linear-gradient(to right, rgb(153, 82, 224), rgb(250, 227, 215), rgb(255, 102, 230))',
              }}
            />
          </>
        )}
        <button
          disabled={disabled}
          className={`${
            disabled ? disabledButtonClassName : defaultButtonClassName
          } px-3 py-2.5 gap-2 w-full relative`}
          data-cy="ai-edit-button"
          onClick={onClick}
          aria-label="Edit with AI"
        >
          <img
            src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/${
              disabled ? 'ai-wand-disabled.svg' : 'ai-wand.svg'
            }`}
            alt="Edit with AI"
            className="w-4 h-4"
          />
          <span className="text-base font-semibold leading-5 text-nowrap">
            Edit with AI
          </span>
        </button>
      </div>
    </div>
  );
};
